import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Shield, 
  Lock, 
  Users, 
  Award,
  BarChart3,
  CheckCircle,
  XCircle,
  ArrowDown,
  Eye,
  FileText,
  AlertTriangle,
  Layers
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const publicLayerPages = [
  "Homepage (silent authority positioning)",
  "Systems Page (architecture explanation)",
  "Certification Overview (no pricing details)",
  "Governance & Enforcement (high-level)",
  "Founder Manifesto",
  "Investor Access Request",
  "Operator Application",
];

const publicLayerRules = [
  "No dashboards",
  "No promises",
  "No funnels",
  "No 'join now'",
];

const applicantPages = [
  "Operator Readiness Assessment",
  "Authority Diagnostic (scored)",
  "Application Status Page",
  "Conditional Acceptance Notice",
];

const certificationComponents = [
  "License Status Indicator",
  "Certification Tier",
  "Audit Schedule",
  "Enforcement Notices",
  "Required Actions",
];

const moduleContents = [
  "Institutional doctrine",
  "Operating frameworks",
  '"Authority Rationale" (why this is enforced)',
  "Locked progression",
  "Timed exams",
  "Auto-fail conditions",
];

const investorMetrics = [
  "License Count",
  "Revenue per License",
  "Enforcement Events",
  "Churn",
  "Audit Pass Rate",
];

const GovernancePage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="governance-page">
      <SEOHead 
        title="System Governance - OnPoint Authority Systems"
        description="Authority infrastructure architecture: Public layer, applicant gating, certification portal, and investor access governance."
        url="https://onpointauthority.com/governance"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="governance-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Authority Infrastructure
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              System<br />
              <span className="text-primary">Governance</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              Four distinct access layers. One unified authority system. 
              No fragmentation. No duct tape. No LMS nonsense.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Layer 1: Public Authority */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="public-layer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/20 border border-primary/30 flex items-center justify-center">
                  <span className="text-sm font-mono text-primary">01</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Unauthenticated</p>
                  <h2 className="font-serif text-3xl font-light text-foreground">Public Authority Layer</h2>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6">
                <span className="text-primary font-medium">Purpose:</span> Establish legitimacy, signal scarcity, 
                pre-qualify buyers without selling.
              </p>

              <div className="mb-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Pages:</p>
                <ul className="space-y-2">
                  {publicLayerPages.map((page, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <Eye className="h-3 w-3 text-primary" />
                      {page}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-card/5 border border-white/10 p-6 mb-6">
                <p className="text-xs text-red-400 uppercase tracking-wider mb-4">Key Rules:</p>
                <div className="grid grid-cols-2 gap-3">
                  {publicLayerRules.map((rule, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <XCircle className="h-4 w-4 text-red-400" />
                      {rule}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/30 p-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Outcome:</p>
                <p className="text-xl font-serif text-primary">
                  Visitors self-select up or out.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Flow Arrow */}
      <div className="flex justify-center py-4">
        <ArrowDown className="h-8 w-8 text-primary/50" />
      </div>

      {/* Layer 2: Applicant/Pre-License */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="applicant-layer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                  <span className="text-sm font-mono text-yellow-400">02</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Gated — Light</p>
                  <h2 className="font-serif text-3xl font-light text-foreground">Applicant / Pre-License Layer</h2>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6">
                <span className="text-yellow-400 font-medium">Purpose:</span> Filter operators before certification.
              </p>

              <div className="mb-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Access Method:</p>
                <ul className="space-y-1 text-sm text-foreground mb-4">
                  <li>• Webflow Memberships</li>
                  <li>• Invite or application approval</li>
                </ul>
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Pages:</p>
                <ul className="space-y-2">
                  {applicantPages.map((page, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <FileText className="h-3 w-3 text-yellow-400" />
                      {page}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-card/5 border border-white/10 p-6 mb-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">Logic:</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-sm text-foreground">Pass → invite to certification</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20">
                    <XCircle className="h-4 w-4 text-red-400" />
                    <span className="text-sm text-foreground">Fail → waitlisted or rejected</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">Everything logged</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 p-6">
                <p className="text-xl font-serif text-yellow-400">
                  This is where authority begins.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Flow Arrow */}
      <div className="flex justify-center py-4 bg-[#050505]">
        <ArrowDown className="h-8 w-8 text-primary/50" />
      </div>

      {/* Layer 3: Certification & Licensing Portal */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="certification-layer">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/20 border border-primary/30 flex items-center justify-center">
                <span className="text-sm font-mono text-primary">03</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Core System</p>
                <h2 className="font-serif text-3xl font-light text-foreground">Certification & Licensing Portal</h2>
              </div>
            </div>
            <p className="text-xl font-serif text-primary mb-4">This is the heart of the business.</p>
            <p className="text-muted-foreground">
              Role-based login. License status check on every page.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Operator Dashboard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">OPERATOR DASHBOARD</h3>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Components:</p>
              <ul className="space-y-2">
                {certificationComponents.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <div className="w-1 h-1 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Certification Modules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Award className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-mono text-primary">CERTIFICATION MODULES</h3>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Each module contains:</p>
              <ul className="space-y-2">
                {moduleContents.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <div className="w-1 h-1 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Enforcement System */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card/5 border border-red-500/20 p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <h3 className="text-sm font-mono text-red-400">ENFORCEMENT SYSTEM</h3>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Internal + Operator Visible:</p>
              <ul className="space-y-2 mb-4">
                {["Violation notices", "Remediation steps", "Revocation logic", "Immutable logs"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <Shield className="h-3 w-3 text-red-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 bg-primary/10 border border-primary/30 p-6"
          >
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Critical Rule:</p>
            <p className="text-xl font-serif text-foreground mb-2">
              Content access ≠ license continuity.
            </p>
            <p className="text-sm text-primary">
              This alone separates you from education companies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Flow Arrow */}
      <div className="flex justify-center py-4">
        <ArrowDown className="h-8 w-8 text-primary/50" />
      </div>

      {/* Layer 4: Investor/Board View */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="investor-layer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <span className="text-sm font-mono text-blue-400">04</span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Read-Only</p>
                  <h2 className="font-serif text-3xl font-light text-foreground">Investor / Board View</h2>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6">
                <span className="text-blue-400 font-medium">Purpose:</span> Prove scalability without exposure.
              </p>

              <div className="mb-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Access:</p>
                <ul className="space-y-1 text-sm text-foreground">
                  <li>• Invite-only</li>
                  <li>• Separate role permissions</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-card/5 border border-white/10 p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                  <h3 className="text-sm font-mono text-blue-400">METRICS DASHBOARD</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {investorMetrics.map((metric, i) => (
                    <div key={i} className="bg-white/5 p-3 text-center">
                      <p className="text-xs text-muted-foreground">{metric}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 p-6">
                <p className="text-lg font-serif text-foreground mb-2">
                  No documents. No storytelling.
                </p>
                <p className="text-xl font-serif text-blue-400">
                  Just proof.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* System Flow */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="system-flow">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              End-to-End
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              System <span className="text-primary">Flow</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Authority Layers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-primary mb-4">Authority Infrastructure Stack</p>
              <ul className="space-y-3">
                {["Authority Design", "Certification Gate", "Enforcement Layer", "Governance Protocol"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* License Lifecycle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/5 border border-white/10 p-6"
            >
              <p className="text-xs font-mono text-primary mb-4">License Governance Cycle</p>
              <ul className="space-y-3">
                {["Application", "Certification", "License Grant", "Audit & Enforcement"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card/5 border border-white/10 p-8 max-w-4xl mx-auto"
          >
            <p className="text-xs font-mono text-primary mb-6 text-center">End-to-End Authority Flow</p>
            <div className="space-y-4">
              {[
                "Public Visitor",
                "Authority Content",
                "Application Gate",
                "Certification Access",
                "License Issued",
                "Ongoing Enforcement",
                "Metrics Surface to Investors",
              ].map((step, i, arr) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-mono text-primary">{i + 1}</span>
                  </div>
                  <p className="text-foreground">{step}</p>
                  {i < arr.length - 1 && (
                    <ArrowDown className="h-4 w-4 text-primary/50 ml-auto" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-primary mt-8 font-serif italic">
              Everything lives inside one unified instance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="governance-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Layers className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="font-serif text-4xl font-light text-foreground mb-6">
              One System.<br />
              <span className="text-primary">Complete Authority.</span>
            </h2>
            <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
              No fragmentation. No duct tape. No LMS nonsense.
              Every layer enforces authority.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="governance-cta-btn"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GovernancePage;
